import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  API_BASE_URL,
  CTX_KEY,
  PAYMENT_TOKEN_KEY,
  RECORDS_KEY,
  customerFetch,
  clearPaymentSession,
  readSession,
  persistSession,
} from '../api/config';
import { PageShell } from '../components/Layout';
import { IconCheck, IconDownload, IconSearch, IconSpinner, IconX } from '../components/Icons';
import {
  buildFullByKeysPayload,
  fetchFullPaidRecords,
  generateHtmlReport,
  triggerHtmlOpenAndDownload,
} from '../utils/report';

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [errorMsg, setErrorMsg] = useState('');
  const [reportHtml, setReportHtml] = useState('');
  const [htmlFilename, setHtmlFilename] = useState('Kharedivikri_Report.html');
  const [paidTxnid, setPaidTxnid] = useState('');
  const [downloadsNote, setDownloadsNote] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    const urlParams = new URLSearchParams(window.location.search);
    const txnidUrl = urlParams.get('txnid');
    const hash = urlParams.get('hash');
    const tokenFromUrl = urlParams.get('token');

    const run = async () => {
      let confirmedTxnid = txnidUrl || '';
      try {
        if (tokenFromUrl) persistSession(PAYMENT_TOKEN_KEY, tokenFromUrl);

        if (txnidUrl && hash) {
          const params = {};
          urlParams.forEach((v, k) => { params[k] = v; });
          const vRes = await fetch(`${API_BASE_URL}/payu/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
          });
          const vData = await vRes.json();
          if (!vData.verified) {
            setStatus('error');
            setErrorMsg('Payment verification failed. Contact support.');
            return;
          }
          persistSession(PAYMENT_TOKEN_KEY, vData.token || '');
          if (vData.txnid) confirmedTxnid = String(vData.txnid);
        }

        const records = JSON.parse(readSession(RECORDS_KEY, '[]'));
        const ctx = JSON.parse(readSession(CTX_KEY, '{}'));
        const paymentToken = readSession(PAYMENT_TOKEN_KEY);

        if (!records.length || !ctx.query) {
          setStatus('error');
          setErrorMsg('Session expired. Please search again.');
          return;
        }

        setStatus('generating');
        const htmlFn = `Kharedivikri_Report_${ctx.query}.html`;
        setHtmlFilename(htmlFn);

        const fullBody = buildFullByKeysPayload(paymentToken, ctx, records);
        if (!fullBody) {
          setStatus('error');
          setErrorMsg('Payment token missing. Contact support.');
          return;
        }

        const fullRes = await fetchFullPaidRecords(fullBody);
        if (!fullRes.ok) {
          let msg = 'Could not load paid records.';
          try {
            const errJson = await fullRes.json();
            msg = errJson.error || msg;
          } catch (_) {}
          setStatus('error');
          setErrorMsg(msg);
          return;
        }

        const fullJson = await fullRes.json().catch(() => ({}));
        const fullRecords = Array.isArray(fullJson.results) ? fullJson.results : [];
        if (!fullRecords.length) {
          setStatus('error');
          setErrorMsg('No records returned after payment.');
          return;
        }

        const html = generateHtmlReport({ records: fullRecords, ctx });
        setReportHtml(html);
        triggerHtmlOpenAndDownload(html, htmlFn);
        setPaidTxnid(confirmedTxnid);

        customerFetch(`${API_BASE_URL}/customer/session/me`)
          .then(async (r) => {
            if (!r.ok) return;
            const j = await r.json().catch(() => ({}));
            if (j.customer?.hasPaidPurchases) {
              setDownloadsNote('Saved to your account — revisit My Reports anytime with the same email and phone.');
            }
          })
          .catch(() => {});

        setStatus('done');
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.message || 'Report generation failed.');
      }
    };

    run();
  }, []);

  const goNewSearch = () => {
    clearPaymentSession();
    navigate('/', { replace: true });
  };

  return (
    <PageShell>
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="bg-white border-2 border-forest shadow-stamp p-8 md:p-10">
          {(status === 'verifying' || status === 'generating') && (
            <>
              <IconSpinner className="w-12 h-12 text-forest mx-auto mb-6" />
              <h1 className="font-serif text-2xl text-forest font-semibold">
                {status === 'verifying' ? 'Confirming payment…' : 'Building your report…'}
              </h1>
              <p className="text-sm text-ink/60 mt-2">Please keep this tab open.</p>
            </>
          )}

          {status === 'done' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-moss/10 border-2 border-moss flex items-center justify-center text-moss">
                <IconCheck className="w-8 h-8" />
              </div>
              <h1 className="font-serif text-3xl text-forest font-semibold">Payment complete</h1>
              {paidTxnid && (
                <p className="text-xs text-ink/50 mt-2 break-all">Txn: {paidTxnid}</p>
              )}
              <p className="text-sm text-ink/60 mt-4">
                Your HTML report should open in a new tab and download automatically.
              </p>
              {downloadsNote && (
                <p className="text-xs bg-parchment border border-sand p-3 mt-4 text-ink/70">{downloadsNote}</p>
              )}
              <div className="mt-8 space-y-3">
                <button type="button" onClick={() => triggerHtmlOpenAndDownload(reportHtml, htmlFilename)} className="btn-primary w-full">
                  <IconDownload className="w-5 h-5" />
                  Download again
                </button>
                <button type="button" onClick={goNewSearch} className="btn-outline w-full">
                  <IconSearch className="w-5 h-5" />
                  New search
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-clay/10 border-2 border-clay flex items-center justify-center text-clay">
                <IconX className="w-8 h-8" />
              </div>
              <h1 className="font-serif text-2xl text-forest font-semibold">Something went wrong</h1>
              <p className="text-clay text-sm mt-3">{errorMsg}</p>
              <div className="mt-8 space-y-3">
                <button type="button" onClick={() => navigate(-1)} className="btn-primary w-full">Go back</button>
                <button type="button" onClick={() => navigate('/')} className="btn-outline w-full">Home</button>
              </div>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
}
